import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useEffect } from 'react'
import { BsArrowLeftShort, BsArrowRightShort } from 'react-icons/bs'
import nextI18nextConfig from '../../next-i18next.config'
import { NEXT_REPONSES } from '../constants/next'
import { createServerApolloClient } from '../dsl/client'
import { Button, SectionTitle } from '../features/base-ui'
import { Layout } from '../features/layouts/Layout'
import { NewsCard } from '../features/webpage'
import { useDataProvider } from '../hooks/dataProvider'
import { getAllAds, getLatestNews, getServices } from '../lib'
import { getTags } from '../lib/tag'
import config from '../../config.json'
import { SchemaMarkupNewsList } from '../features/utility/SchemaMarkupNewsList'
import { createServiceMenu } from '../utils/menu'

function Results({
    news = [],
    menuServices,
    q,
    tag,
    author,
    content,
    mainContent = '',
    service,
    latest,
    page = 1,
    lastPage = false,
    advenys,
    buildTime,
}) {
    const { t } = useTranslation(['common', 'news'])
    const { setData } = useDataProvider()
    useEffect(
        () => {
            setData({
                menuItems: createServiceMenu(menuServices),
                siteAds: advenys?.filter((a) => a.location.indexOf('site_') === 0) ?? [],
                buildTime,
            })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [menuServices]
    )

    let title = 'آخرین اخبار'
    if(author) title = `اخبار درج شده توسط ${author}`
    if(q || tag || content || service) title = `${q ?? tag ?? content ?? service}`

    return (
        <main className="px-4 lg:px-2 xl:px-0 md:container mx-auto">
            <Head>
                {tag && <link rel="canonical" href={`${config.core.rootUrl}/tag/${tag}`} />}
                <title>{title}</title>
                <meta property="og:title" content={title}></meta>
                <meta name="description" content={`میتوانید بروزترین ${title} را اینجا بخوانید`}></meta>
                <meta property="og:description" content={`میتوانید بروزترین ${title} را اینجا بخوانید`}></meta>
            </Head>
            <section>
                <div className="grid lg:grid-cols-3 xl:grid-cols-4 gap-10 py-10">
                    <div className="lg:col-span-2 xl:col-span-3 order-1 h-max">
                        <SectionTitle className="my-6">
                            {q ?? tag ?? service ?? author ?? content ?? 'آخرین اخبار'}
                        </SectionTitle>
                        {mainContent && <div className="dynamic-content"
                            style={{
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                backgroundColor: '#fff',
                                padding: 10,
                                marginBottom: 10,
                                borderRadius: '0.25rem',
                            }}
                            dangerouslySetInnerHTML={{ __html: mainContent }}></div>}
                        <div className="grid gap-4 h-full mb-6">
                            {news.map((item, idx) => (
                                <NewsCard
                                    key={idx}
                                    type="horizental"
                                    shadow="lg"
                                    titleLines={1}
                                    titleHtmlEl="h5"
                                    horizentalImageSize="narrow"
                                    contentStyle={{ fontSize: '0.9rem' }}
                                    news={item}
                                    showAbst
                                />
                            ))}
                            {!news?.length && 'موردی یافت نشد'}
                        </div>
                        {!!news.length && (
                            <div className="inline-flex gap-2">
                                <Button
                                    type="linkButton"
                                    hoverColor={
                                        page <= 1 ? 'transparent' : 'darkGray'
                                    }
                                    color={page <= 1 ? 'lighterGray' : 'white'}
                                    disabled={page <= 1}
                                    className="flex-nowrap inline-flex"
                                    href={`/search?${q ? `q=${q}` : ''}${tag ? `tag=${tag}` : ''
                                        }${service ? `service=${service}` : ''}${latest ? 'latest' : ''
                                        }&p=${page - 1}`}
                                >
                                    <BsArrowRightShort
                                        style={{ display: 'inline' }}
                                    />
                                    {t('صفحه ی قبلی', { ns: 'common' })}
                                </Button>

                                <Button
                                    type="linkButton"
                                    hoverColor={
                                        lastPage ? 'transparent' : 'darkGray'
                                    }
                                    color={lastPage ? 'lighterGray' : 'white'}
                                    disabled={lastPage}
                                    className="flex-nowrap inline-flex"
                                    href={`/search?${q ? `q=${q}` : ''}${tag ? `tag=${tag}` : ''
                                        }${service ? `service=${service}` : ''}${latest ? 'latest' : ''
                                        }&p=${page + 1}`}
                                >
                                    {t('صفحه ی بعد', { ns: 'common' })}
                                    <BsArrowLeftShort
                                        style={{ display: 'inline' }}
                                    />
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className="flex order-2 gap-4 flex-wrap justify-center"></div>
                </div>
            </section>
            <SchemaMarkupNewsList newsList={news} />
        </main>
    )
}
Results.Layout = Layout

export default Results

export async function getServerSideProps({ locale, query }) {
    if (
        !query.q &&
        !query.tag &&
        !query.service &&
        !query.author &&
        !query.content &&
        typeof query.latest === 'undefined'
    )
        return NEXT_REPONSES.NOT_FOUND_R300

    for (const k of Object.keys(query))
        query[k] = decodeURIComponent(query[k])

    const client = await createServerApolloClient()

    const services = await getServices(client, locale)
    const menuServices = await getServices(client, locale, ['showInMenu'], {
        includeChildren: true,
        preloadedData: services,
    })

    let options = {
        page: Number(query.p) || 1,
        limit: 13,
    }

    if (query.author) options.author = [query.author]
    if (query.tag) options.tags = [query.tag]
    if (query.q) options.title = [query.q]
    if (query.content) options.content = [query.content]
    if (query.service) {
        const currentService = services?.find((e) => e.title === query.service)
        if (!currentService) return NEXT_REPONSES.NOT_FOUND_R300
        options.services = [currentService.id]
    }
    const news = await getLatestNews(client, locale, options)
    let advenys = await getAllAds(client, locale)

    let mainContent = ''
    if (query.tag) {
        const tag = await getTags(client, { title: decodeURIComponent(query.tag) })
        mainContent = tag?.[0]?.content ?? ''
    }

    return {
        props: {
            menuServices,
            news: news.slice(0, 12),
            q: query.q ?? null,
            tag: query.tag ?? null,
            author: query.author ?? null,
            content: query.content ?? null,
            service: query.service ?? null,
            page: Number(query.p) || 1,
            latest: typeof query.latest !== 'undefined',
            lastPage: news.length !== 13,
            advenys,
            mainContent,
            ...(await serverSideTranslations(
                locale,
                ['common', 'news'],
                nextI18nextConfig
            )),
            buildTime: new Date().toISOString(),
        },
    }
}
